import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';

let unreadSummaryRequest = null;

function getAll() {
  return fetchWrapper.get(`${config.apis.api.url}/channels/`, { injectToken: true });
}

function getByOwner(userId) {
  return fetchWrapper.get(`${config.apis.api.url}/channels/user/${userId}/`, { injectToken: true });
}

function getOne(channelId) {
  return fetchWrapper.get(`${config.apis.api.url}/channels/${channelId}/`, { injectToken: true });
}

function getMemberOf(userIdOrMe = 'me') {
  return fetchWrapper.get(`${config.apis.api.url}/channels/member/${userIdOrMe}/`, { injectToken: true });
}

function getMembershipSummary() {
  return fetchWrapper.get(`${config.apis.api.url}/channels/membership/summary`, { injectToken: true });
}

function getContentAudienceEligibility() {
  return fetchWrapper.get(`${config.apis.api.url}/channels/content-audience-eligibility`, { injectToken: true });
}

// Unified conversations (owned + joined) summary
// Expected shape: { data: [ { channelId, title, avatar, ownerUsername, lastMessage, lastActivity, unreadCount, readState } ] }
function getConversations() {
  return fetchWrapper.get(`${config.apis.api.url}/channels/conversations`, { injectToken: true });
}

function getUnreadSummary() {
  if (!unreadSummaryRequest) {
    unreadSummaryRequest = fetchWrapper
      .get(`${config.apis.api.url}/channels/conversations/unread-summary`, { injectToken: true })
      .finally(() => {
        unreadSummaryRequest = null;
      });
  }

  return unreadSummaryRequest;
}

function create(payload) {
  return fetchWrapper.post(`${config.apis.api.url}/channels/`, { injectToken: true, body: payload });
}

function update(channelId, payload) {
  return fetchWrapper.put(`${config.apis.api.url}/channels/${channelId}/`, { injectToken: true, body: payload });
}

function remove(channelId) {
  return fetchWrapper.delete(`${config.apis.api.url}/channels/${channelId}/`, { injectToken: true });
}

function join(channelId) {
  return fetchWrapper.post(`${config.apis.api.url}/channels/${channelId}/member`, { injectToken: true });
}

function leave(channelId) {
  return fetchWrapper.delete(`${config.apis.api.url}/channels/${channelId}/member`, { injectToken: true });
}

function removeMember(channelId, memberId) {
  const normalizedMemberId = memberId ? String(memberId) : '';
  const directUrl = `${config.apis.api.url}/channels/${channelId}/member/${encodeURIComponent(normalizedMemberId)}`;
  const fallbackUrl = `${config.apis.api.url}/channels/${channelId}/member?member_id=${encodeURIComponent(normalizedMemberId)}`;

  return fetchWrapper.delete(directUrl, { injectToken: true })
    .then((result) => {
      if (result?.response?.ok) {
        return result;
      }
      return fetchWrapper.delete(fallbackUrl, { injectToken: true });
    });
}

// Paid subscriptions: create a Stripe Checkout session for the channel
// Backend should return { checkout_url }
function createCheckoutSession(channelId, {
  successUrl,
  cancelUrl,
  forceNonBusinessPurchase,
  productName,
  checkoutLocale,
} = {}) {
  const url = `${config.apis.api.url}/channels/${channelId}/subscribe/checkout-session`;
  return fetchWrapper.post(url, {
    injectToken: true,
    body: {
      success_url: successUrl,
      cancel_url: cancelUrl,
      force_non_business_purchase: forceNonBusinessPurchase === true,
      product_name: productName,
      checkout_locale: checkoutLocale,
    },
  });
}

function getSubscriptionQuote(channelId, { paymentMethodId, buyerCountry, forceNonBusinessPurchase } = {}) {
  const params = new URLSearchParams();
  if (paymentMethodId) params.set('payment_method_id', paymentMethodId);
  if (buyerCountry) params.set('buyer_country', buyerCountry);
  if (forceNonBusinessPurchase === true) params.set('force_non_business_purchase', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchWrapper.get(`${config.apis.api.url}/channels/${channelId}/subscribe/quote${query}`, {
    injectToken: true,
  });
}

function createSubscription(channelId, { paymentMethodId, buyerCountry, forceNonBusinessPurchase } = {}) {
  return fetchWrapper.post(`${config.apis.api.url}/channels/${channelId}/subscribe`, {
    injectToken: true,
    body: {
      payment_method_id: paymentMethodId,
      buyer_country: buyerCountry,
      force_non_business_purchase: forceNonBusinessPurchase === true,
    },
  });
}

function finalizeSubscription(channelId, subscriptionId) {
  return fetchWrapper.post(`${config.apis.api.url}/channels/${channelId}/subscribe/finalize`, {
    injectToken: true,
    body: { stripe_subscription_id: subscriptionId },
  });
}

function cancelSubscription(channelId, { immediate = false } = {}) {
  const url = `${config.apis.api.url}/channels/${channelId}/subscribe/cancel${immediate ? '?immediate=1' : ''}`;
  return fetchWrapper.post(url, { injectToken: true });
}

function ensureSubscription(channelId) {
  const url = `${config.apis.api.url}/channels/${channelId}/subscribe/ensure`;
  return fetchWrapper.post(url, { injectToken: true });
}

function switchSubscriptionBusinessBilling(channelId, { setup_intent_id, payment_method_id } = {}) {
  const url = `${config.apis.api.url}/channels/${channelId}/subscribe/switch-business-billing`;
  return fetchWrapper.post(url, {
    injectToken: true,
    body: {
      setup_intent_id: setup_intent_id ?? null,
      payment_method_id: payment_method_id ?? null,
    },
  });
}

function getOverview(channelId) {
  return fetchWrapper.get(`${config.apis.api.url}/channels/${channelId}/overview`, { injectToken: true });
}

function getMembersList(channelId) {
  return fetchWrapper.get(`${config.apis.api.url}/channels/${channelId}/members`, { injectToken: true });
}

function getSharedMedia(channelId, { limit, type } = {}) {
  const params = new URLSearchParams();
  if (typeof limit === 'number' && Number.isFinite(limit) && limit > 0) {
    params.set('limit', String(limit));
  }
  if (type) {
    params.set('type', type);
  }
  const query = params.toString();
  const url = query
    ? `${config.apis.api.url}/channels/${channelId}/media?${query}`
    : `${config.apis.api.url}/channels/${channelId}/media`;

  return fetchWrapper.get(url, { injectToken: true });
}

function getStickers(channelId) {
  return fetchWrapper.get(`${config.apis.api.url}/channels/${channelId}/stickers`, { injectToken: true });
}

const ChannelsService = {
  getAll,
  getByOwner,
  getOne,
  getMemberOf,
  getMembershipSummary,
  getContentAudienceEligibility,
  getConversations,
  getUnreadSummary,
  create,
  update,
  remove,
  join,
  leave,
  removeMember,
  createCheckoutSession,
  getSubscriptionQuote,
  createSubscription,
  finalizeSubscription,
  cancelSubscription,
  ensureSubscription,
  switchSubscriptionBusinessBilling,
  getOverview,
  getMembersList,
  getSharedMedia,
  getStickers,
};

export default ChannelsService;
